import React, { useState, useEffect } from 'react';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Shield, CheckCircle2, XCircle, FileText, Clock, User } from 'lucide-react';

const AdminVerificationPage = () => {
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchPendingVerifications();
  }, []);

  const fetchPendingVerifications = async () => {
    try {
      const response = await api.get('/verification/pending');
      setPendingVerifications(response.data.pending_verifications);
    } catch (err) {
      console.error('Failed to fetch pending verifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (verificationId) => {
    setProcessing(true);
    try {
      await api.post(`/verification/approve/${verificationId}`);
      alert('Verification approved successfully!');
      await fetchPendingVerifications();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to approve verification');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    setProcessing(true);
    try {
      await api.post(
        `/verification/reject/${selectedVerification.id}`,
        null,
        { params: { rejection_reason: rejectReason } }
      );
      alert('Verification rejected');
      setShowRejectDialog(false);
      setRejectReason('');
      setSelectedVerification(null);
      await fetchPendingVerifications();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reject verification');
    } finally {
      setProcessing(false);
    }
  };

  const openRejectDialog = (verification) => {
    setSelectedVerification(verification);
    setShowRejectDialog(true);
  };

  const getTypeLabel = (type) => {
    const labels = {
      id: 'ID Verification',
      background_check: 'Background Check',
      phone: 'Phone Verification',
      email: 'Email Verification'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-primary-900 mb-2">
            Verification Management
          </h1>
          <p className="text-neutral-600">
            Review and approve user verification documents
          </p>
        </div>
        <Badge className="bg-primary-700 text-white text-lg px-4 py-2">
          {pendingVerifications.length} Pending
        </Badge>
      </div>

      {/* Info Alert */}
      <Alert>
        <Shield className="h-4 w-4" />
        <AlertDescription>
          <strong>Admin Panel:</strong> Review documents carefully. ID verification grants users full platform access.
        </AlertDescription>
      </Alert>

      {/* Pending Verifications */}
      {pendingVerifications.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <CheckCircle2 className="h-16 w-16 text-success-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-neutral-700 mb-2">
              All Caught Up!
            </h3>
            <p className="text-neutral-500">
              No pending verifications to review
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {pendingVerifications.map((verification) => (
            <Card key={verification.id} className="border-2">
              <CardHeader className="bg-neutral-50">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
                      <FileText className="h-6 w-6 text-yellow-700" />
                    </div>
                    <div>
                      <CardTitle className="text-xl mb-1">
                        {getTypeLabel(verification.verification_type)}
                      </CardTitle>
                      <div className="flex items-center gap-3 text-sm text-neutral-600">
                        <span className="flex items-center gap-1">
                          <User className="h-4 w-4" />
                          {verification.user?.first_name} {verification.user?.last_name}
                        </span>
                        <span>•</span>
                        <span>{verification.user?.email}</span>
                      </div>
                      <p className="text-sm text-neutral-500 mt-1">
                        Submitted: {new Date(verification.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300">
                    <Clock className="h-3 w-3 mr-1" />
                    Pending
                  </Badge>
                </div>
              </CardHeader>

              <CardContent className="pt-6">
                <div className="space-y-4">
                  {/* Document Preview */}
                  <div>
                    <p className="text-sm font-medium text-neutral-700 mb-2">Submitted Document:</p>
                    <div className="border-2 border-dashed border-neutral-300 rounded-lg p-8 bg-neutral-50 text-center">
                      {verification.document_data?.startsWith('data:image') ? (
                        <img
                          src={verification.document_data}
                          alt="Verification document"
                          className="max-h-64 mx-auto rounded"
                        />
                      ) : (
                        <div>
                          <FileText className="h-16 w-16 text-neutral-400 mx-auto mb-2" />
                          <p className="text-neutral-600">Document attached</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <Button
                      onClick={() => handleApprove(verification.id)}
                      disabled={processing}
                      className="flex-1 bg-success-600 hover:bg-success-700"
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Approve
                    </Button>
                    <Button
                      onClick={() => openRejectDialog(verification)}
                      disabled={processing}
                      variant="destructive"
                      className="flex-1"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Verification</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejection. This will be shown to the user.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="e.g., Document is blurry, name doesn't match, expired ID"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={processing || !rejectReason.trim()}
            >
              {processing ? 'Rejecting...' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminVerificationPage;
